// src/features/merchantConfig/lib/fieldValidators.js
//
// Per-type validators used by the edit-mode field editors. Validators return
// `null` for "ok" and a human-readable string for "invalid". Empty values are
// allowed at the validator layer — required-ness is enforced (or not) by
// the editor based on the original-value type.
//
// These checks are belt-and-braces: the OMS API will reject malformed bodies
// with INVALID_PARAMETER_VALUE (API doc §6), but catching it client-side
// avoids a round-trip and gives the user immediate feedback.

const URL_RE = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?([+-]\d{2}:\d{2}|Z)?$/;

/**
 * @param {string} value
 * @returns {string | null}
 */
export function validateUrl(value) {
  if (value === "" || value === null || value === undefined) return null;
  if (typeof value !== "string") return "URL must be a string.";
  if (!URL_RE.test(value)) {
    return "Enter a valid URL beginning with http:// or https://.";
  }
  return null;
}

/**
 * @param {string} value
 * @returns {string | null}
 */
export function validateEmail(value) {
  if (value === "" || value === null || value === undefined) return null;
  if (typeof value !== "string") return "Email must be a string.";
  if (!EMAIL_RE.test(value)) {
    return "Enter a valid email address.";
  }
  return null;
}

/**
 * @param {string} value
 * @returns {string | null}
 */
export function validateIsoDateTime(value) {
  if (value === "" || value === null || value === undefined) return null;
  if (typeof value !== "string") return "Date must be a string.";
  if (!ISO_DATETIME_RE.test(value)) {
    return "Enter a valid ISO 8601 datetime (YYYY-MM-DDTHH:mm).";
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return "Date is unparseable.";
  }
  return null;
}

/**
 * @param {unknown} value
 * @returns {string | null}
 */
export function validateNumber(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "Enter a valid number.";
  return null;
}

/**
 * @param {unknown} value
 * @returns {string | null}
 */
export function validateBoolean(value) {
  if (value === null || value === undefined) return null;
  if (typeof value !== "boolean") return "Value must be true or false.";
  return null;
}

/**
 * Convenience dispatcher — picks the right validator from the inferred type
 * tag (see `lib/typeCoercion.inferType`).
 *
 * @param {string} typeTag
 * @param {unknown} value
 * @returns {string | null}
 */
export function validateByType(typeTag, value) {
  switch (typeTag) {
    case "url":
      return validateUrl(value);
    case "email":
      return validateEmail(value);
    case "iso-datetime":
      return validateIsoDateTime(value);
    case "number":
      return validateNumber(value);
    case "boolean":
      return validateBoolean(value);
    case "string":
    case "long-string":
    case "null":
    case "object":
    case "array-of-scalars":
    case "array-of-objects":
    case "unknown":
    default:
      return null;
  }
}
